import { getOwner } from '@ember/owner';
import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task, type TaskInstance } from 'ember-concurrency';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import type {
  Parity,
  ParityInsightHistoricalData,
  ParityInsightSegmentationData,
  ParityScreenshotParity,
} from 'lumen/models';
import type { Source } from 'lumen/models/parity/insight/segmentation-item';
import {
  CHANNEL_TYPES,
  SOURCES,
} from 'lumen/models/parity/insight/segmentation-item';
import type {
  ParityInsightRouteModel as ParityInsightMultiPropertyRouteModel,
  RouteQueryParams,
} from 'lumen/routes/market/parity/insight';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import type {
  MealTypeValues,
  MembershipType,
} from 'lumen/utils/constants/general-constants';
import {
  APIGATEWAY,
  MEMBERSHIPTYPES,
} from 'lumen/utils/constants/general-constants';
import type { CountryCodeWithDefault } from 'lumen/utils/constants/pi-constant-values';
import { DEFAULT_LOS } from 'lumen/utils/constants/pi-constant-values';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import DataFetching from 'lumen/utils/parity/insight/data-fetching';
import {
  type FromDateRange,
  getFromDateRange,
} from 'lumen/utils/parity/insight/get-from-date-range';
import {
  type ExportConfig,
  getUniqueExportConfigs,
} from 'lumen/utils/parity/insight/get-unique-export-configs';
import ConfigResource from 'lumen/utils/parity/resources/config';
import ExportConfigResource from 'lumen/utils/parity/resources/export-config';
import OtaHotelInfoResource from 'lumen/utils/parity/resources/ota-hotel-info';
import SilentTaskError from 'lumen/utils/silent-task-error';

const EXCLUDED_SOURCES = [
  SOURCES.DIRECTLY_SHOPPED,
  SOURCES.TRIVAGO,
  SOURCES.TRIPADVISOR,
  SOURCES.GOOGLEHOTELFINDER,
];

type ParityBaseRequestParams = {
  channel: string[];
  exclude_ota: Source[];
  extract_date: string;
  from_date_range_end?: string;
  from_date_range_start?: string;
  los: number;
  membershiptype: MembershipType;
  namespace: typeof APIGATEWAY;
  ota: string[];
  platform: Platform;
  pos: CountryCodeWithDefault;
  wholesale_rate?: boolean;
};

type ParityIssuesRequestParams = ParityBaseRequestParams & {
  base_membershiptype: MembershipType;
  filter_meta_allow_major: boolean;
  filters?: string;
  group_meta_by: string[];
  historical_extract_date?: string;
  hotel_id: string;
  mealtype: MealTypeValues;
  periods?: number;
  persons: number;
  typeKey: 'parities/analytics';
};

type ParityScreenshotParityRequestParams = ParityBaseRequestParams & {
  subscription_id: string;
  typeKey: 'parityanalytics/screenshot_parities';
};

type IssuesAndScreenshotParities = {
  fromDateRangePerPlatform: Map<Platform, FromDateRange>;
  issues: Map<Platform, Parity[]>;
  screenshotParities: ParityScreenshotParity[][];
};

type ParityInsightRouteModel = ParityInsightMultiPropertyRouteModel & {
  issuesAndScreenshotParities: TaskInstance<
    IssuesAndScreenshotParities | undefined
  >;
};

export default class HotelParityInsightRoute extends Route {
  queryParams = {
    segmentationFilters: { refreshModel: true },
    historicalExtractDate: { refreshModel: true },
    extractDate: { refreshModel: true },
    benchmark: { refreshModel: false },
    dateAggregation: { refreshModel: true },
    sortType: { refreshModel: false },
    sortKey: { refreshModel: false },
    rateType: { refreshModel: true },
    los: { refreshModel: true },
    pos: { refreshModel: true },
    mealPlan: { refreshModel: true },
    showMemberRate: { refreshModel: true },
    fromDateType: { refreshModel: true },
  };

  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  configResource = new ConfigResource(getOwner(this)!, {
    isSingleProperty: true,
  });
  dataFetching = new DataFetching(getOwner(this)!, { isSingleProperty: true });
  exportConfigResource = new ExportConfigResource(getOwner(this)!);
  lastActiveHotelId: string | null = null;
  modelObject = trackedObject<ParityInsightRouteModel>({
    config: this.configResource.fetchResourceTask,
    favourites: null,
    historicalData: {
      isRunning: true,
      value: null,
    } as TaskInstance<ParityInsightHistoricalData>,
    issuesAndScreenshotParities: {
      isRunning: true,
      value: null,
    } as TaskInstance<IssuesAndScreenshotParities | undefined>,
    segmentationData: {
      isRunning: true,
      value: null,
    } as TaskInstance<ParityInsightSegmentationData>,
  });
  otaHotelInfoResource = new OtaHotelInfoResource(getOwner(this)!);

  previousParams: RouteQueryParams | null = null;

  requestIssuesAndScreenshotParitiesTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        // OtaHotelInfos have to be fully loaded before fetching Parities and ScreenshotParities
        await this.otaHotelInfoResource.fetchResource();

        const exportConfigResponse =
          await this.exportConfigResource.fetchResourceTask.perform({
            subscription_id: this.hotelAndMarketService.hotelOrMarket.id!,
            extract_date: params.extractDate,
            date_aggregation: params.dateAggregation,
            from_date_type: params.fromDateType,
          });

        if (!exportConfigResponse[0]) {
          return;
        }

        // The from_dates are already limited to the selected fromDateType by the export_config request
        const uniqueExportConfigs = getUniqueExportConfigs(
          exportConfigResponse[0],
        );

        const issueRequests = new Map<Platform, Promise<Parity[]>[]>();
        const screenshotRequests: Promise<ParityScreenshotParity[]>[] = [];
        // Keep track of fromDates per Platform for gap filtering and the from date range indication
        const fromDatesPerPlatforms = new Map<Platform, Set<string>>();

        uniqueExportConfigs.forEach((exportConfig) => {
          // Construct requests per ExportConfig
          this.fetchIssuesPerExportConfig(
            params,
            exportConfig,
            issueRequests,
            screenshotRequests,
            fromDatesPerPlatforms,
          );
        });

        // Concurrently fetch issues for all platforms
        const issuesPerPlatform = new Map<Platform, Parity[]>();
        const platformPromises = Array.from(issueRequests.entries()).map(
          async ([platform, requests]) => {
            const issuesArrays = await Promise.all(requests);
            issuesPerPlatform.set(platform, issuesArrays.flat());
          },
        );
        await Promise.all(platformPromises);

        // Filter out gaps in from_dates (https://lighthouseintelligence.atlassian.net/browse/PI-1155?focusedCommentId=287741)
        const filteredIssuesPerPlatform = new Map<Platform, Parity[]>();
        issuesPerPlatform.forEach((issues, platform) => {
          const fromDatesForPlatform = fromDatesPerPlatforms.get(platform)!;
          const filteredIssues = issues.filter((issue) =>
            fromDatesForPlatform.has(issue.period.day.formattedDate),
          );
          filteredIssuesPerPlatform.set(platform, filteredIssues);
        });

        const fromDateRangePerPlatform = new Map<Platform, FromDateRange>();
        fromDatesPerPlatforms.forEach((fromDates, platform) => {
          const fromDateRange = getFromDateRange(fromDates);
          if (fromDateRange) {
            fromDateRangePerPlatform.set(platform, fromDateRange);
          }
        });

        return {
          fromDateRangePerPlatform,
          issues: filteredIssuesPerPlatform,
          screenshotParities: await Promise.all(screenshotRequests),
        };
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getAPIErrorMessage(error, this.intl));
      }
    },
  );

  async afterModel(model: ParityInsightRouteModel): Promise<void> {
    // Silent fallback in case config fails to load: so page is still accessible (but validation is skipped)
    const config = await model.config.last?.catch(() => null);

    if (config) {
      this.transition.validateParityInsightQueryParamsAfter(
        this.paramsFor(this.routeName),
        config,
      );
    }
  }

  beforeModel(transition: Transition): void {
    const hasSwitchedHotel =
      this.lastActiveHotelId !== null &&
      this.lastActiveHotelId !== this.hotelAndMarketService.hotel.id;

    if (!this.access.hasParityAnalytics) {
      this.transition.redirectToEnabledTab(transition);
      return;
    }

    if (hasSwitchedHotel) {
      this.clearCachedData();
      // LOS, POS and filters differ per subscription
      const { los, pos, segmentationFilters } =
        DEFAULT_QP_VALUES.market.parity.insight;
      // Preserve browser history with transitionTo (vs replaceWith)
      this.router.transitionTo({
        queryParams: { los, pos, segmentationFilters },
      });
      return;
    }

    this.transition.validateParityInsightQueryParamsBefore(
      this.paramsFor(this.routeName),
    );
  }

  clearCachedData(): void {
    this.dataFetching.clearCachedData();

    this.modelObject.favourites = null;
  }

  // eslint-disable-next-line max-params
  fetchIssuesPerExportConfig(
    params: RouteQueryParams,
    exportConfig: ExportConfig,
    issueRequests: Map<Platform, Promise<Parity[]>[]>,
    screenshotRequests: Promise<ParityScreenshotParity[]>[],
    fromDatesPerPlatforms: Map<Platform, Set<string>>,
  ): void {
    const customLos = params.los !== DEFAULT_LOS ? params.los : undefined;

    const sharedFilterParams: Pick<
      ParityBaseRequestParams,
      'channel' | 'exclude_ota' | 'ota' | 'wholesale_rate'
    > = {
      exclude_ota: [],
      ota: [],
      channel: [],
    };

    // Filter rules: https://docs.google.com/document/d/1VMC9UGjLnKMHidOZFLB8CUB1Hgrtad7-TCnIyr4dWJ4/edit#heading=h.g4dmmytnwvkb
    if (params.segmentationFilters.length) {
      const {
        source,
        channel,
        channel_type: channelType,
      } = JSON.parse(params.segmentationFilters) as Record<string, string[]>;

      if (source) {
        if (source.includes(SOURCES.DIRECTLY_SHOPPED)) {
          const excludedOtas = EXCLUDED_SOURCES.filter(
            (src) => !source.includes(src),
          );
          sharedFilterParams.exclude_ota = excludedOtas;
        } else {
          // BRANDDOTCOM rates need to be included manually
          sharedFilterParams.ota = [OTA_IDS.BRANDDOTCOM, ...source];
        }
      }

      sharedFilterParams.channel = channel || sharedFilterParams.channel;
      sharedFilterParams.wholesale_rate =
        channelType && channelType[0] === CHANNEL_TYPES.OTHER;
    }

    const {
      los: defaultLos,
      platform,
      persons,
      extractDate,
      fromDateRangeStart,
      fromDateRangeEnd,
      fromDates,
    } = exportConfig;

    if (!fromDatesPerPlatforms.has(platform)) {
      fromDatesPerPlatforms.set(platform, new Set(fromDates));
    } else {
      const existingFromDates = fromDatesPerPlatforms.get(platform)!;
      fromDates.forEach((date) => existingFromDates.add(date));
    }

    const parityAnalyticsOTAIds = this.hotelAndMarketService.hotel
      .getParityAnalyticsOTAs(platform)
      .map((ota) => ota.id!);

    const issueParams: ParityIssuesRequestParams = {
      ...sharedFilterParams,
      // Always filter on PA OTAs until BE stops returning Platform unspecified (RATE-888)
      // Intersect with OTAs from source filter
      // If no ota filter is present, return all OTAs for the platform
      ota:
        sharedFilterParams.ota.length > 0
          ? parityAnalyticsOTAIds.filter((otaId) =>
              sharedFilterParams.ota.includes(otaId),
            )
          : parityAnalyticsOTAIds,
      typeKey: 'parities/analytics',
      hotel_id: this.hotelAndMarketService.hotel.id!,
      extract_date: extractDate,
      persons,
      from_date_range_start: fromDateRangeStart,
      from_date_range_end: fromDateRangeEnd,
      filter_meta_allow_major: true,
      group_meta_by: ['ota', 'channel'],
      platform,
      namespace: APIGATEWAY,
      membershiptype: MEMBERSHIPTYPES.PUBLIC,
      los: customLos ?? defaultLos, // Pass custom LOS if set, otherwise use default LOS from exportConfig
      mealtype: params.mealPlan,
      pos: params.pos,
      base_membershiptype: params.showMemberRate
        ? MEMBERSHIPTYPES.MEMBER_ONLY
        : MEMBERSHIPTYPES.PUBLIC,
    };

    // Add request to already existing requests for this platform (if any)
    issueRequests.set(platform, [
      ...(issueRequests.get(platform) ?? []),
      this.store.query<Parity>('parity', issueParams),
    ]);

    const screenshotParityParams: ParityScreenshotParityRequestParams = {
      ...sharedFilterParams,
      namespace: APIGATEWAY,
      membershiptype: MEMBERSHIPTYPES.PUBLIC,
      typeKey: 'parityanalytics/screenshot_parities',
      subscription_id: this.hotelAndMarketService.hotel.id!,
      extract_date: extractDate,
      from_date_range_start: fromDateRangeStart,
      from_date_range_end: fromDateRangeEnd,
      platform,
      los: customLos ?? defaultLos, // Pass custom LOS if set, otherwise use default LOS from exportConfig
      pos: params.pos,
    };

    screenshotRequests.push(
      this.store.query<ParityScreenshotParity>(
        'parity/screenshot-parity',
        screenshotParityParams,
      ),
    );
  }

  model(params: RouteQueryParams): ParityInsightRouteModel {
    // Check whether QPs have changes. On initial load previousParams is null and the checks below should all pass
    // No cache key: a new data QP must be compared here and sent in utils/parity/insight/data-fetching.ts or it goes stale
    const isHistoricalExtractDateUpdate =
      this.previousParams === null ||
      params.historicalExtractDate !==
        this.previousParams.historicalExtractDate;

    const isExtractDateUpdate =
      this.previousParams === null ||
      params.extractDate !== this.previousParams.extractDate;

    const isDateAggregationUpdate =
      this.previousParams === null ||
      params.dateAggregation !== this.previousParams.dateAggregation;

    const isSelectionsUpdate =
      params.showMemberRate !== this.previousParams?.showMemberRate ||
      params.los !== this.previousParams?.los ||
      params.pos !== this.previousParams?.pos ||
      params.mealPlan !== this.previousParams?.mealPlan ||
      params.rateType !== this.previousParams?.rateType ||
      params.fromDateType !== this.previousParams?.fromDateType;

    // Check whether only filters have been changed so we can use storedUnfilteredResponse (no extra request required)
    // When all filters all filters are cleared (length of filters = 0) we can return false as we don't need to make a filtered request
    const isFilterUpdate =
      params.segmentationFilters.length &&
      params.segmentationFilters !== this.previousParams?.segmentationFilters;

    this.previousParams = params;

    // Fetch config once on first page load
    if (this.lastActiveHotelId !== this.hotelAndMarketService.hotel.id) {
      void this.modelObject.config.perform();
    }

    this.lastActiveHotelId = this.hotelAndMarketService.hotelOrMarket.id;

    if (this.modelObject.favourites === null) {
      const parameters = {
        hotel: this.hotelAndMarketService.hotelOrMarket.id!,
        page_type: 'PA',
      };

      void this.store
        .query<Insightpagesfavourite>('insightpagesfavourite', parameters)
        .then((favourites) => {
          this.modelObject.favourites = [...favourites];
        });
    }

    // If there is no cache (page load) or all updates are required, just do EVERYTHING!
    const requiresAllUpdates =
      (isHistoricalExtractDateUpdate && isExtractDateUpdate) ||
      isSelectionsUpdate ||
      isDateAggregationUpdate;

    if (!this.dataFetching.unfilteredCache || requiresAllUpdates) {
      // Request filtered/unfiltered historical data with general function which will decide which request to make for us
      this.modelObject.historicalData =
        this.dataFetching.requestHistoricalDataTask(params);

      // Load segmentation data filtered/unfiltered using general function that merges/caches it for us
      this.modelObject.segmentationData =
        this.dataFetching.requestSegmentationDataTask.perform(params);

      // Special case: If you have a page load with filters, make sure to also cache in the background the unfiltered version for it
      if (this.dataFetching.hasActiveFilters(params)) {
        void this.dataFetching.requestUnfilteredHistoricalDataTask.perform(
          params,
        );
      }
    } else if (isFilterUpdate) {
      // If it's simply a filter update - we want a filtered request for all data
      this.modelObject.segmentationData =
        this.dataFetching.requestSegmentationDataFromCacheTask.perform(params);

      // Filter update situation also requires new historical data
      this.modelObject.historicalData =
        this.dataFetching.requestHistoricalDataTask(params);
    } else if (isExtractDateUpdate) {
      // If there was only an extract date update - don't update that history chart, that data doesn't change!
      this.modelObject.segmentationData =
        this.dataFetching.requestSegmentationDataTask.perform(params);

      // If filtered request, return appropriate cache item
      if (this.dataFetching.hasActiveFilters(params)) {
        this.modelObject.historicalData = {
          value: this.dataFetching.filteredHistoricalCache,
        } as TaskInstance<ParityInsightHistoricalData>;
      } else {
        this.modelObject.historicalData = {
          value: this.dataFetching.unfilteredHistoricalCache,
        } as TaskInstance<ParityInsightHistoricalData>;
      }
    } else if (isHistoricalExtractDateUpdate) {
      // If there is a historical update - only update that history chart
      this.modelObject.historicalData =
        this.dataFetching.requestHistoricalDataTask(params);

      // When filters active, and changing the scope. Also cache the unfiltered version in the background. Otherwise removing filters results in changing history data
      if (this.dataFetching.hasActiveFilters(params)) {
        void this.dataFetching.requestUnfilteredHistoricalDataTask.perform(
          params,
        );
        this.modelObject.segmentationData = {
          value: this.dataFetching.filteredCache,
        } as TaskInstance<ParityInsightSegmentationData>;
      } else {
        this.modelObject.segmentationData = {
          value: this.dataFetching.unfilteredCache,
        } as TaskInstance<ParityInsightSegmentationData>;
      }
    } else if (
      this.dataFetching.filteredCache &&
      this.dataFetching.filteredHistoricalCache &&
      this.dataFetching.hasActiveFilters(params)
    ) {
      // If there is no update and there are caches, then return those caches if they are available, otherwise we return nothing
      this.modelObject.segmentationData = {
        value: this.dataFetching.filteredCache,
      } as TaskInstance<ParityInsightSegmentationData>;
      this.modelObject.historicalData = {
        value: this.dataFetching.filteredHistoricalCache,
      } as TaskInstance<ParityInsightHistoricalData>;
    } else if (
      this.dataFetching.unfilteredCache &&
      this.dataFetching.unfilteredHistoricalCache
    ) {
      this.modelObject.segmentationData = {
        value: this.dataFetching.unfilteredCache,
      } as TaskInstance<ParityInsightSegmentationData>;
      this.modelObject.historicalData = {
        value: this.dataFetching.unfilteredHistoricalCache,
      } as TaskInstance<ParityInsightHistoricalData>;
    }

    // Since extract_date is not part of a ParityScreenshotParity ID, we unload the store when the extract dates changes
    if (isExtractDateUpdate) {
      this.store.unloadAll('parity/screenshot-parity');
    }

    this.modelObject.issuesAndScreenshotParities =
      this.requestIssuesAndScreenshotParitiesTask.perform(params);

    return this.modelObject;
  }
}
